// Columna derecha del explorer (`.col-detail` del diseño): el compromiso elegido — hero, la fila de
// acciones, el bloque Detalle, el de Contexto y el histórico del caso.
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, X, Zap } from 'lucide-react';
import { Button, Modal } from '@/kit';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import {
    creditArrearsCaption,
    formatPromiseDay,
    formatPromiseMoney,
    PROMISE_STATUS_LABEL,
    PROMISE_STATUS_TONE,
    TONE_CHIP_CLASS,
} from '../format';
import type { PaymentPromiseRow } from '../types';
import { ChannelChip } from './PromisesTable';

/** Cuántas entradas del histórico caben en el panel, como el mock. */
const HISTORY_LIMIT = 6;

/** Los dos avisos de las acciones que todavía no tienen backend que las ejecute. */
const KEPT_NOTICE = {
    title: 'Marcar cumplido',
    body: 'Se aplicará el pago al crédito y se cerrará el compromiso.',
};

const BROKEN_NOTICE = {
    title: 'Marcar incumplido',
    body: 'El compromiso se marca incumplido. Aparece con prioridad máxima en la Agenda.',
};

const SECTION_TITLE =
    'mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground';

/** El ancla que abre la Agenda, con el alto y el aire del botón primario de la fila de acciones. */
const ACTION_LINK =
    'inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-2.5 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Una fila `clave · valor` del detalle (`.info-row` del mock). */
function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-[100px_1fr] gap-1.5 py-[3px] text-[11.5px]">
            <span className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </span>
            <span>{children}</span>
        </div>
    );
}

export interface PromiseDetailProps {
    /** `null` = no hay compromiso elegido, porque los filtros no dejaron ninguno. */
    promise: PaymentPromiseRow | null;
}

export function PromiseDetail({ promise }: PromiseDetailProps) {
    const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);

    if (promise === null) {
        return (
            <MasterDetailEmpty
                title="Ningún compromiso seleccionado"
                sub="Elige un compromiso de la lista para ver su detalle, su contexto y el histórico del caso."
            />
        );
    }

    const lastContact = promise.caseHistory.at(0);
    const history = promise.caseHistory.slice(0, HISTORY_LIMIT);

    return (
        <section aria-label="Detalle del compromiso" className="flex min-h-0 flex-1 flex-col">
            <div className="flex-none border-b border-border px-3.5 py-3">
                <div className="mb-1 flex items-start justify-between gap-2">
                    <span className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Compromiso
                    </span>
                    <span
                        className={`inline-flex items-center whitespace-nowrap rounded-full border px-[5px] py-px text-[9.5px] font-medium ${TONE_CHIP_CLASS[PROMISE_STATUS_TONE[promise.status]]}`}
                    >
                        {PROMISE_STATUS_LABEL[promise.status]}
                    </span>
                </div>
                <div className="font-mono text-[15px] font-semibold leading-tight">
                    {/* eslint-disable-next-line no-restricted-syntax -- el folio del compromiso ES el título de la ficha en el mock; no hay nombre que resolver */}
                    {promise.promiseId}
                </div>
                <div className="mt-1 text-[12px]">
                    <Link
                        to="/credit/customers"
                        search={{ cliente: promise.customerId }}
                        className="text-primary hover:underline"
                    >
                        {promise.customerName}
                    </Link>
                </div>
                <div className="text-[10.5px] text-muted-foreground">
                    {creditArrearsCaption(promise.creditId, promise.arrearsDays)}
                </div>
            </div>

            <div className="flex flex-none flex-wrap gap-1.5 px-3.5 py-2">
                <Link
                    to="/collections/$section"
                    params={{ section: 'agenda' }}
                    className={ACTION_LINK}
                >
                    <Zap className="size-2.5" aria-hidden />
                    Abrir caso en Agenda
                </Link>
                <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<Check className="size-2.5" aria-hidden />}
                    onClick={() => {
                        setNotice(KEPT_NOTICE);
                    }}
                >
                    Marcar cumplido
                </Button>
                <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<X className="size-2.5" aria-hidden />}
                    onClick={() => {
                        setNotice(BROKEN_NOTICE);
                    }}
                >
                    Marcar incumplido
                </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3 text-[11.5px]">
                <div className={SECTION_TITLE}>Detalle</div>
                <InfoRow label="Fecha">{formatPromiseDay(promise.promisedDay)}</InfoRow>
                <InfoRow label="Monto">
                    <span className="font-semibold tabular-nums">
                        {formatPromiseMoney(promise.promisedAmountCents)}
                    </span>
                </InfoRow>
                <InfoRow label="Canal">
                    <ChannelChip channel={promise.channel} />
                </InfoRow>
                <InfoRow label="Registrado">{formatPromiseDay(promise.registeredDay)}</InfoRow>

                <div className={`${SECTION_TITLE} mt-3`}>Contexto</div>
                <InfoRow label="DPI mora">
                    {`${String(promise.arrearsDays)} días · ${formatPromiseMoney(promise.arrearsAmountCents)}`}
                </InfoRow>
                <InfoRow label="Últ. contacto">
                    {lastContact === undefined
                        ? 'Sin contacto reciente'
                        : `${lastContact.channel} · ${lastContact.outcome} · ${formatPromiseDay(lastContact.day)}`}
                </InfoRow>

                <div className={`${SECTION_TITLE} mt-3`}>Histórico del caso</div>
                {history.length === 0 ? (
                    <div className="text-[11px] italic text-muted-foreground">Sin historial.</div>
                ) : (
                    <div className="text-[11px]">
                        {history.map((contact, index) => (
                            <div
                                key={`${contact.day}-${contact.channel}-${String(index)}`}
                                className="border-b border-border/60 py-1"
                            >
                                <div className="text-[10px] text-muted-foreground">
                                    {`${formatPromiseDay(contact.day)} · ${contact.channel} · ${contact.outcome}`}
                                </div>
                                <div className="text-[11px]">{contact.note || '—'}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Modal
                isOpen={notice !== null}
                onClose={() => {
                    setNotice(null);
                }}
                title={notice?.title}
                size="sm"
            >
                <div className="text-[13px] leading-relaxed">{notice?.body}</div>
                <div className="mt-4 flex justify-end">
                    <Button
                        onClick={() => {
                            setNotice(null);
                        }}
                    >
                        Entendido
                    </Button>
                </div>
            </Modal>
        </section>
    );
}
